import { useState, useCallback, useEffect, useRef } from 'react'
import { zipSync } from 'fflate'
import {
  Heart,
  MessageCircle,
  Eye,
  ExternalLink,
  ImageOff,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  Loader2,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

// ─── Public types ────────────────────────────────────────────────────────────

export type PostCardData = {
  id: string
  authorUsername: string
  authorId?: string | null
  authorProfileUrl?: string | null
  platform: string
  mediaType: 'image' | 'video' | 'carousel' | null
  postType?: string | null
  detectedBySource?: string | null
  caption: string | null
  postUrl: string | null
  mediaUrl: string | null
  thumbnailUrl: string | null
  r2MediaKey: string | null
  r2ThumbnailKey?: string | null
  carouselMedia: { cdnUrl: string; r2Key: string | null }[] | null
  mediaWidth: number | null
  mediaHeight: number | null
  likes: number | null
  comments: number
  views: number
  postedAt: string | null
  createdAt: string
}

export interface PostCardProps {
  post: PostCardData
  /** Optional status badge rendered in the lightbox header */
  statusBadge?: React.ReactNode
  /** Optional actions bar rendered between caption and engagement in lightbox */
  lightboxActions?: React.ReactNode
  /** Highlight card with emerald border */
  isRecent?: boolean
}

// ─── Constants ───────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787'
const R2_CDN = import.meta.env.VITE_R2_CDN_URL || ''

const AVATAR_GRADIENTS = [
  'from-pink-500 to-rose-500',
  'from-orange-400 to-pink-500',
  'from-violet-500 to-purple-600',
  'from-sky-400 to-blue-600',
  'from-emerald-400 to-teal-600',
  'from-amber-400 to-orange-500',
]

function avatarGradient(username: string): string {
  let hash = 0
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]
}

function formatCount(n: number): string {
  if (n < 0) return '–'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return n.toString()
}

// ─── Engagement ──────────────────────────────────────────────────────────────

type EngagementLevel = 'viral' | 'high' | 'medium' | 'low'

interface EngagementScore {
  rate: number
  level: EngagementLevel
}

function computeEngagement(post: PostCardData): EngagementScore | null {
  if (post.views <= 0) return null
  const rate = (((post.likes ?? 0) + post.comments) / post.views) * 100
  const level: EngagementLevel =
    rate >= 8 ? 'viral' : rate >= 3 ? 'high' : rate >= 1 ? 'medium' : 'low'
  return { rate, level }
}

const ENGAGEMENT_CLASSES: Record<EngagementLevel, string> = {
  viral: 'text-pink-500 dark:text-pink-400',
  high: 'text-emerald-600 dark:text-emerald-400',
  medium: 'text-amber-600 dark:text-amber-400',
  low: 'text-muted-foreground',
}

const ENGAGEMENT_LABELS: Record<EngagementLevel, string> = {
  viral: 'Viral 🔥',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}

// ─── Media URL helpers ───────────────────────────────────────────────────────

function getMediaUrl(post: { id: string; r2MediaKey: string | null }): string {
  if (R2_CDN && post.r2MediaKey) return `${R2_CDN}/${post.r2MediaKey}`
  return `${API_BASE}/api/admin/social-listening/mentions/${post.id}/media`
}

function getThumbnailUrl(post: {
  id: string
  r2ThumbnailKey?: string | null
  r2MediaKey: string | null
}): string {
  if (R2_CDN && post.r2ThumbnailKey) return `${R2_CDN}/${post.r2ThumbnailKey}`
  if (R2_CDN && post.r2MediaKey) return `${R2_CDN}/${post.r2MediaKey}`
  return `${API_BASE}/api/admin/social-listening/mentions/${post.id}/media?type=thumb`
}

function getCarouselSlideUrl(post: { id: string }, index: number, r2Key: string | null): string {
  if (R2_CDN && r2Key) return `${R2_CDN}/${r2Key}`
  return `${API_BASE}/api/admin/social-listening/mentions/${post.id}/carousel/${index}`
}

// ─── Type labels ─────────────────────────────────────────────────────────────

const MEDIA_TYPE_LABELS: Record<string, string> = {
  video: 'Reels',
  image: 'Foto',
  carousel: 'Carrossel',
}

const POST_TYPE_LABELS: Record<string, string> = {
  reel: 'Reels',
  story: 'Story',
  feed: 'Feed',
  carousel: 'Carrossel',
  tiktok: 'TikTok',
}

function getProfileUrl(post: Pick<PostCardData, 'platform' | 'authorUsername'>): string {
  if (post.platform === 'tiktok') return `https://tiktok.com/@${post.authorUsername}`
  return `https://instagram.com/${post.authorUsername}`
}

function getTypeLabel(post: PostCardData): string | null {
  if (post.detectedBySource === 'story_mention') return 'Story'
  if (post.postType && POST_TYPE_LABELS[post.postType]) return POST_TYPE_LABELS[post.postType]
  if (post.mediaType && MEDIA_TYPE_LABELS[post.mediaType]) return MEDIA_TYPE_LABELS[post.mediaType]
  return null
}

// ─── Thumbnail ───────────────────────────────────────────────────────────────

/** Fallback aspect ratios per media type when dimensions are unknown */
const FALLBACK_RATIOS: Record<string, string> = {
  video: '9 / 16',
  image: '4 / 5',
  carousel: '4 / 5',
}

function getAspectRatio(post: PostCardData): string {
  if (post.mediaWidth && post.mediaHeight) return `${post.mediaWidth} / ${post.mediaHeight}`
  return FALLBACK_RATIOS[post.mediaType ?? ''] ?? '1 / 1'
}

function Thumbnail({ post }: { post: PostCardData }) {
  const [imgError, setImgError] = useState(false)
  const [useFallback, setUseFallback] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [fallbackToVideo, setFallbackToVideo] = useState(false)

  const isVideo = post.mediaType === 'video'
  const hasRealThumbnail = !!(post.r2ThumbnailKey || post.thumbnailUrl)
  const showVideoFrame = isVideo && (!hasRealThumbnail || fallbackToVideo)

  const src = useFallback ? post.thumbnailUrl || post.mediaUrl || '' : getThumbnailUrl(post)
  const ratio = getAspectRatio(post)

  const handleError = useCallback(() => {
    if (!useFallback) {
      setUseFallback(true)
    } else if (isVideo && !fallbackToVideo) {
      // Thumbnail URLs all failed — try loading the video first frame
      setFallbackToVideo(true)
    } else {
      setImgError(true)
    }
  }, [useFallback, isVideo, fallbackToVideo])

  // Catch 200 OK responses that aren't valid images (empty body, wrong content, etc.)
  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget
      if (img.naturalWidth === 0) handleError()
    },
    [handleError]
  )

  if (!post.mediaUrl && !post.r2MediaKey) {
    return (
      <div className="w-full aspect-square bg-muted flex items-center justify-center text-muted-foreground">
        <ImageOff className="h-6 w-6 opacity-40" />
      </div>
    )
  }

  if (showVideoFrame && !videoError) {
    const videoSrc = getMediaUrl(post)
    return (
      <div className="relative w-full bg-black overflow-hidden" style={{ aspectRatio: ratio }}>
        <video
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setVideoError(true)}
        >
          <source src={`${videoSrc}#t=0.001`} type="video/mp4" />
        </video>
      </div>
    )
  }

  if (imgError || videoError) {
    return (
      <div
        className="w-full bg-muted flex items-center justify-center text-muted-foreground"
        style={{ aspectRatio: ratio }}
      >
        <ImageOff className="h-6 w-6 opacity-40" />
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={`Post de @${post.authorUsername}`}
      loading="lazy"
      decoding="async"
      className="w-full object-cover"
      style={{ aspectRatio: ratio }}
      onLoad={handleLoad}
      onError={handleError}
    />
  )
}

// ─── Video hover preview ─────────────────────────────────────────────────────

function VideoHoverPreview({
  src,
  videoRef,
}: {
  src: string
  videoRef: React.RefObject<HTMLVideoElement | null>
}) {
  return (
    <video
      ref={videoRef}
      src={src}
      muted
      playsInline
      loop
      preload="none"
      className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-100"
    />
  )
}

// ─── Carousel viewer ─────────────────────────────────────────────────────────

function CarouselViewer({ post }: { post: PostCardData }) {
  const [index, setIndex] = useState(0)
  const [imgError, setImgError] = useState(false)
  const slides = post.carouselMedia ?? []
  const total = slides.length

  const getSlideSrc = (i: number) => getCarouselSlideUrl(post, i, slides[i]?.r2Key ?? null)

  const updateIndex = useCallback((i: number) => setIndex(i), [])

  const prev = useCallback(() => updateIndex(Math.max(0, index - 1)), [index, updateIndex])
  const next = useCallback(
    () => updateIndex(Math.min(total - 1, index + 1)),
    [index, total, updateIndex]
  )

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev()
      else if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [prev, next])

  if (imgError || total === 0) {
    return (
      <div className="flex items-center justify-center w-full h-full text-white/40">
        <ImageOff className="h-10 w-10" />
      </div>
    )
  }

  return (
    <div className="relative flex items-center justify-center w-full h-full">
      <img
        key={index}
        src={getSlideSrc(index)}
        alt={`Slide ${index + 1}`}
        className="w-full h-full object-contain"
        onError={() => setImgError(true)}
      />
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            disabled={index === 0}
            className="absolute left-2 p-1.5 rounded-full bg-black/50 text-white disabled:opacity-30 hover:bg-black/70 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            disabled={index === total - 1}
            className="absolute right-2 p-1.5 rounded-full bg-black/50 text-white disabled:opacity-30 hover:bg-black/70 transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
          <div className="absolute bottom-3 flex gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => updateIndex(i)}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === index ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Lightbox ────────────────────────────────────────────────────────────────

function LightboxContent({
  post,
  onClose,
  sessionKey,
  statusBadge,
  lightboxActions,
}: {
  post: PostCardData
  onClose: () => void
  sessionKey: number
  statusBadge?: React.ReactNode
  lightboxActions?: React.ReactNode
}) {
  const [imgError, setImgError] = useState(false)
  const [useFallback, setUseFallback] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const postedAt = post.postedAt ? new Date(post.postedAt) : null
  const typeLabel = getTypeLabel(post)

  const handleDownload = useCallback(async () => {
    const date = post.postedAt
      ? new Date(post.postedAt).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10)
    const baseName = `@${post.authorUsername}_${date}`

    setIsDownloading(true)
    try {
      if (post.mediaType === 'carousel') {
        const total = post.carouselMedia?.length ?? 0
        const results = await Promise.all(
          Array.from({ length: total }, async (_, i) => {
            const url = `${API_BASE}/api/admin/social-listening/mentions/${post.id}/carousel/${i}`
            const res = await fetch(url, { credentials: 'include' })
            if (!res.ok) return null
            const buf = await res.arrayBuffer()
            const ext = res.headers.get('content-type')?.split('/')[1]?.split(';')[0] || 'jpg'
            return { buf, ext, index: i }
          })
        )
        const files: Record<string, Uint8Array> = {}
        for (const item of results) {
          if (!item) continue
          files[`${baseName}_${item.index + 1}.${item.ext}`] = new Uint8Array(item.buf)
        }
        const zip = zipSync(files)
        const blobUrl = URL.createObjectURL(
          new Blob([zip.buffer as ArrayBuffer], { type: 'application/zip' })
        )
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = `${baseName}.zip`
        a.click()
        URL.revokeObjectURL(blobUrl)
      } else {
        const url = `${API_BASE}/api/admin/social-listening/mentions/${post.id}/media`
        const res = await fetch(url, { credentials: 'include' })
        if (!res.ok) throw new Error('Download failed')
        const blob = await res.blob()
        const ext = blob.type.split('/')[1]?.split(';')[0] || 'bin'
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = `${baseName}.${ext}`
        a.click()
        URL.revokeObjectURL(blobUrl)
      }
    } catch (e) {
      console.error('Download failed', e)
    } finally {
      setIsDownloading(false)
    }
  }, [post])

  const baseMediaSrc = useFallback ? post.mediaUrl || '' : getMediaUrl(post)
  const mediaSrc =
    post.mediaType === 'video'
      ? `${baseMediaSrc}${baseMediaSrc.includes('?') ? '&' : '?'}v=${sessionKey}`
      : baseMediaSrc
  const thumbSrc = useFallback ? post.thumbnailUrl || post.mediaUrl || '' : getThumbnailUrl(post)

  const handleError = useCallback(() => {
    if (!useFallback) setUseFallback(true)
    else setImgError(true)
  }, [useFallback])

  const isReel = post.mediaType === 'video'

  return (
    <div className="flex flex-col sm:flex-row max-h-dvh sm:h-[min(85vh,750px)] overflow-y-auto sm:overflow-hidden">
      {/* Media pane */}
      <div
        className={cn(
          'relative bg-black flex items-center justify-center overflow-hidden w-full',
          'max-h-[50vh] sm:max-h-none sm:h-full sm:w-auto',
          isReel ? 'sm:aspect-9/16' : 'sm:aspect-square'
        )}
      >
        {post.mediaType === 'carousel' && post.carouselMedia?.length ? (
          <CarouselViewer post={post} />
        ) : imgError ? (
          <div className="flex items-center justify-center w-full h-full text-white/40">
            <ImageOff className="h-10 w-10" />
          </div>
        ) : post.mediaType === 'video' ? (
          <video
            src={mediaSrc}
            poster={thumbSrc}
            controls
            playsInline
            autoPlay
            loop
            className="w-full h-full object-cover"
            onError={handleError}
          />
        ) : (
          <img src={mediaSrc} alt="" className="w-full h-full object-cover" onError={handleError} />
        )}
      </div>

      {/* Info panel */}
      <div className="flex flex-col border-l border-border bg-card w-full sm:w-85 shrink-0 sm:self-stretch overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border shrink-0">
          <a
            href={getProfileUrl(post)}
            target="_blank"
            rel="noopener noreferrer"
            className="h-8 w-8 rounded-full shrink-0 hover:opacity-80 transition-opacity overflow-hidden"
          >
            <Avatar className="h-8 w-8">
              {post.authorId && post.authorProfileUrl ? (
                <AvatarImage
                  src={`${API_BASE}/api/admin/social-listening/author/${post.authorId}/avatar`}
                  alt={post.authorUsername}
                />
              ) : null}
              <AvatarFallback
                className={cn(
                  'bg-linear-to-br text-white text-xs font-bold',
                  avatarGradient(post.authorUsername)
                )}
              >
                {post.authorUsername[0]?.toUpperCase() ?? '?'}
              </AvatarFallback>
            </Avatar>
          </a>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <a
                href={getProfileUrl(post)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold truncate hover:underline"
              >
                @{post.authorUsername}
              </a>
              {statusBadge}
              {typeLabel && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">
                  {typeLabel}
                </Badge>
              )}
            </div>
            {postedAt && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatDistanceToNow(postedAt, { addSuffix: true, locale: ptBR }).replace(
                  'cerca de ',
                  ''
                )}
              </p>
            )}
          </div>

          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-1 shrink-0">
              {(post.r2MediaKey || post.mediaUrl) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={isDownloading}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {isDownloading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Baixar mídia</TooltipContent>
                </Tooltip>
              )}
              {post.postUrl && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={post.postUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Abrir no Instagram</TooltipContent>
                </Tooltip>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </TooltipProvider>
        </div>

        {/* Caption */}
        <div className="flex-1 overflow-y-auto p-4 min-h-0">
          {post.caption ? (
            <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-line">
              {post.caption}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground italic">Sem legenda</p>
          )}
        </div>

        {/* Actions slot */}
        {lightboxActions}

        {/* Engagement footer */}
        <div className="shrink-0 border-t border-border">
          <div className="flex items-center gap-4 px-4 py-2.5 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Heart className="h-4 w-4" />
              {post.likes === null ? (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">–</span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-xs">
                      Creator deixou as curtidas ocultas
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                formatCount(post.likes)
              )}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-4 w-4" />
              {formatCount(post.comments)}
            </span>
            {post.views > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {formatCount(post.views)}
              </span>
            )}
            {(() => {
              const eng = computeEngagement(post)
              if (!eng) return null
              return (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          'ml-auto text-xs font-medium cursor-default',
                          ENGAGEMENT_CLASSES[eng.level]
                        )}
                      >
                        {ENGAGEMENT_LABELS[eng.level]} · {eng.rate.toFixed(1)}%
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-56 text-xs">
                      <p className="font-medium mb-1">Taxa de engajamento</p>
                      <p className="text-muted-foreground">
                        (curtidas + comentários) ÷ visualizações × 100
                      </p>
                      <p className="text-muted-foreground mt-1">
                        Viral 🔥 ≥ 8% · Alta ≥ 3% · Média ≥ 1% · Baixa &lt; 1%
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main card ───────────────────────────────────────────────────────────────

export function PostCard({ post, statusBadge, lightboxActions, isRecent }: PostCardProps) {
  const [open, setOpen] = useState(false)
  const [sessionKey, setSessionKey] = useState(0)
  const postedAt = post.postedAt ? new Date(post.postedAt) : null
  const hasMedia = !!(post.mediaUrl || post.r2MediaKey)
  const isVideo = post.mediaType === 'video'
  const engagement = computeEngagement(post)
  const typeLabel = getTypeLabel(post)

  const videoRef = useRef<HTMLVideoElement>(null)

  const canHover = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches

  const handleMouseEnter = useCallback(() => {
    if (isVideo && canHover) videoRef.current?.play()
  }, [isVideo, canHover])

  const handleMouseLeave = useCallback(() => {
    if (isVideo && canHover && videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [isVideo, canHover])

  useEffect(() => {
    if (open && isVideo && videoRef.current) {
      videoRef.current.pause()
    }
  }, [open, isVideo])

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isOpen) setSessionKey((k) => k + 1)
        setOpen(isOpen)
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={cn(
            'group rounded-[10px] bg-card text-left w-full',
            isRecent
              ? 'border-2 border-emerald-500 dark:border-emerald-500/60'
              : 'border border-border',
            'shadow-resting hover:shadow-raised',
            'transition-all duration-200 hover:-translate-y-px',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'dark:bg-card/60 dark:backdrop-blur-xl'
          )}
          style={
            isRecent
              ? {
                  boxShadow:
                    '0 0 0 4px rgba(16, 185, 129, 0.18), 0 0 0 1px rgba(16, 185, 129, 0.35)',
                }
              : undefined
          }
        >
          {/* Thumbnail area */}
          <div className="relative overflow-hidden rounded-t-[9px]">
            {hasMedia ? (
              <Thumbnail post={post} />
            ) : (
              <div className="w-full aspect-square bg-muted flex items-center justify-center text-muted-foreground">
                <ImageOff className="h-6 w-6 opacity-40" />
              </div>
            )}

            {isVideo && hasMedia && (
              <VideoHoverPreview src={getMediaUrl(post)} videoRef={videoRef} />
            )}

            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 pointer-events-none" />
          </div>

          {/* Info strip */}
          <div className="border-t border-border px-2.5 py-2 space-y-0.5">
            <div className="flex items-center justify-between gap-1.5">
              <p className="text-[13px] font-medium text-foreground truncate min-w-0">
                @{post.authorUsername}
              </p>
              <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
                <span className="flex items-center gap-0.5 text-xs">
                  <Heart className="h-3 w-3" />
                  {post.likes === null ? (
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">–</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          Creator deixou as curtidas ocultas
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    formatCount(post.likes)
                  )}
                </span>
                <span className="flex items-center gap-0.5 text-xs">
                  <MessageCircle className="h-3 w-3" />
                  {formatCount(post.comments)}
                </span>
                {post.views > 0 && (
                  <span className="flex items-center gap-0.5 text-xs">
                    <Eye className="h-3 w-3" />
                    {formatCount(post.views)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between gap-1">
              <p className="text-xs text-muted-foreground truncate">
                {typeLabel ? `${typeLabel} · ` : ''}
                {postedAt
                  ? formatDistanceToNow(postedAt, { addSuffix: true, locale: ptBR }).replace(
                      'cerca de ',
                      ''
                    )
                  : ''}
              </p>
              {engagement && (
                <span
                  className={cn(
                    'text-xs font-medium shrink-0',
                    ENGAGEMENT_CLASSES[engagement.level]
                  )}
                >
                  {ENGAGEMENT_LABELS[engagement.level]}
                </span>
              )}
            </div>
          </div>
        </button>
      </DialogTrigger>

      <DialogContent
        className="p-0 w-[95vw] sm:w-auto! max-w-[calc(100vw-2rem)]! overflow-hidden border-0 gap-0"
        showCloseButton={false}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <LightboxContent
          post={post}
          onClose={() => setOpen(false)}
          sessionKey={sessionKey}
          statusBadge={statusBadge}
          lightboxActions={lightboxActions}
        />
      </DialogContent>
    </Dialog>
  )
}
